import React, { useEffect, useState } from 'react';
import { ArrowRight, Zap, Lock, BarChart3 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import CountdownTimer from './CountdownTimer';

const HeroSection: React.FC = () => {
  const [animate, setAnimate] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    setAnimate(true);
  }, []);

  return (
    <section 
      id="hero" 
      className="relative min-h-screen pt-20 flex flex-col justify-center"
      role="banner"
      aria-labelledby="hero-title"
    >
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div className={`transition-all duration-1000 ${animate ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="inline-block mb-4 px-4 py-1.5 gradient-border rounded-full bg-light-card dark:bg-dark-card">
              <span className="text-sm font-medium text-primary dark:text-primary-light">
                {t('hero.launch')}
              </span>
            </div>
            <h1 
              id="hero-title"
              className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight"
            >
              {t('hero.title')}
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-400 mb-8 leading-relaxed max-w-xl">
              {t('hero.subtitle')}
            </p>
            
            <div className="mb-8">
              <CountdownTimer />
            </div>
            
            <div className="flex flex-wrap gap-4">
              <button 
                className="gradient-bg px-6 py-3 rounded-lg flex items-center font-medium text-white hover:opacity-90 transition-opacity shadow-lg shadow-primary/20 dark:shadow-primary/10 focus:outline-none focus:ring-2 focus:ring-primary"
                aria-label={t('hero.learnMore')}
              >
                {t('hero.learnMore')}
                <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
              </button>
              <button 
                className="px-6 py-3 gradient-border rounded-lg flex items-center font-medium hover:bg-light-card dark:hover:bg-dark-card transition-colors focus:outline-none focus:ring-2 focus:ring-primary"
                aria-label={t('hero.viewDocs')}
              >
                {t('hero.viewDocs')}
              </button>
            </div>
          </div>

          <div className={`transition-all duration-1000 delay-300 ${animate ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="gradient-border rounded-2xl p-6 md:p-8 blur-bg shadow-xl shadow-primary/5 dark:shadow-primary/2">
              <div className="space-y-6">
                <FeatureCard 
                  icon={<Zap className="h-6 w-6 text-primary dark:text-primary-light" aria-hidden="true" />}
                  title={t('hero.feature1')}
                  description={t('hero.feature1.desc')}
                  delay="delay-100"
                />
                <FeatureCard 
                  icon={<Lock className="h-6 w-6 text-secondary dark:text-secondary-light" aria-hidden="true" />}
                  title={t('hero.feature2')}
                  description={t('hero.feature2.desc')}
                  delay="delay-200"
                />
                <FeatureCard 
                  icon={<BarChart3 className="h-6 w-6 text-primary dark:text-primary-light" aria-hidden="true" />}
                  title={t('hero.feature3')}
                  description={t('hero.feature3.desc')}
                  delay="delay-300"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  delay: string;
}

const FeatureCard: React.FC<FeatureCardProps> = ({ icon, title, description, delay }) => {
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setAnimate(true), 500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div 
      className={`flex items-start space-x-4 transition-all duration-700 ${delay} ${animate ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}
      role="listitem"
    >
      <div className="flex-shrink-0 p-3 gradient-border rounded-lg bg-light-card dark:bg-dark-card">
        {icon}
      </div>
      <div>
        <h3 className="text-lg font-semibold text-black dark:text-white mb-1">{title}</h3>
        <p className="text-gray-600 dark:text-gray-400 text-sm">{description}</p>
      </div>
    </div>
  );
};

export default HeroSection;